import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subject, concat, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, startWith, switchMap, tap } from 'rxjs/operators';

import { FilesUploadComponent } from '@shared/components/files-upload/files-upload.component';

import { environment } from '../../../../../environments/environment';
import { NoSpaceOnlyValidation } from '../../../../shared/custom-validators/no-space-only-validation';
import { FileModuleEnum } from '../../../../shared/enum/file-url.enum';
import { ProductTypeEnum } from '../../../../shared/enum/product-type.enum';
import { NewRequestStatusEnum } from '../../../../shared/enum/request-status.enum';
import { NewRequestTypeEnum, RequestPageModesEnum } from '../../../../shared/enum/request-step.enum';
import { ShelfPages } from '../../../../shared/enum/td-store-page.enum';
import { ShelfItems } from '../../../../shared/models';
import { ShelfInventoryRequestViewResponse } from '../../../../shared/models/shelf-inventory-request.model';
import {
  ShelfFixAssetRequestViewResponse,
  ShelfRequestViewResponse
} from '../../../../shared/models/shelf-request.model';
import { ShelfTypesService } from '../../../../shared/services/shelf-types.service';
import { AppStates } from '../../../../shared/store/state/app.states';
import { getFileUploadObj } from '../../../../shared/utils/get-file-name-from-ref-id-util';
import { getSelectShelf, getSelectShelfPage } from '../../../../shared/utils/get-select-by-page-util';
import { AddShelfTypeComponent } from '../add-shelf-type/add-shelf-type.component';

@Component({
  selector: 'app-shelf-info',
  templateUrl: './shelf-info.component.html',
  styleUrls: ['./shelf-info.component.scss']
})
export class ShelfInfoComponent extends OnDestroyMixin implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() parentForm: UntypedFormGroup;
  @Input() submitted: boolean;
  @Input() saved: boolean;
  @Input() mode: RequestPageModesEnum;
  @Input() page: ShelfPages;
  @Input() is: boolean;
  @Input() shelfType: ProductTypeEnum;

  @Output() uploaded: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('shelfImage', { static: false }) shelfImage: FilesUploadComponent;

  public shelfTypeSearchLoading: boolean;
  public shelfTypeSearchInput$ = new Subject<string>();
  public shelfTypeList$: Observable<ShelfItems[]>;
  public status: NewRequestStatusEnum;
  public listOfChange: string[] = [];

  public propImageAllowedTypes: any = '(pdf|png|jpg|jpe?g)';
  public propImageSize = 512000;
  public propImageAllowedExt: any = '(.pdf|.png|.jpg|.jpe?g)';
  public propImageDescriptionTxt = 'Format file .pdf, .jpg, jpeg, .png (Size up to 500KB)';
  public propImageFileSizeErrorTxt = 'Size up to 500 KB.';
  public propImageFileTypeErrorTxt = 'Incorrect format (allow only format file .pdf, .jpg, .jpeg, .png).';

  private localStore: Observable<any>;
  private type: NewRequestTypeEnum;
  private shelfRequestView$: Observable<ShelfRequestViewResponse>;
  private shelfInventoryRequestView$: Observable<ShelfInventoryRequestViewResponse>;
  private shelfFixAssetRequestView$: Observable<ShelfFixAssetRequestViewResponse>;

  constructor(
    private readonly fb: UntypedFormBuilder,
    protected readonly modalService: BsModalService,
    private readonly shelfTypeService: ShelfTypesService,
    private readonly store: Store<AppStates>
  ) {
    super();
  }

  ngAfterViewInit(): void {
    this.shelfImage.uploaded.pipe(untilComponentDestroyed(this)).subscribe(flag => {
      this.uploaded.emit(flag);
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  ngOnInit() {
    this.type = NewRequestTypeEnum.NEW;
    this.localStore = this.store.pipe(untilComponentDestroyed(this));

    this.parentForm.addControl('shelfInfo', this.createForm());
    this.loadShelfTypeList('');

    if (this.mode !== RequestPageModesEnum.REQUEST_CREATE) {
      if (this.page === ShelfPages.SHELF_REQUEST_INVENTORY || this.page === ShelfPages.SHELF_INVENTORY_EDIT) {
        this.setShelfInventoryInfoValue();
      }

      if (this.page === ShelfPages.SHELF_FIX_ASSET_EDIT || this.page === ShelfPages.SHELF_REQUEST_FIX_ASSET) {
        this.setShelfFixAssetInfoValue();
      }

      if (this.page === ShelfPages.SHELF_EDIT || this.page === ShelfPages.SHELF_REQUEST) {
        this.setShelfInfoValue();
      }
    }

    if (this.shelfType === ProductTypeEnum.INVENTORY) {
      const fileLimitMegaBytes = 20;
      const oneMegaBytes = 1_048_576;
      const fileSizeLimit = oneMegaBytes * fileLimitMegaBytes;
      const fileSizeLimitText = `${fileSizeLimit / oneMegaBytes}`;
      this.propImageAllowedTypes = '(pdf)';
      this.propImageSize = fileSizeLimit;
      this.propImageAllowedExt = '(.pdf)';
      this.propImageDescriptionTxt = `Format file .pdf (Size up to ${fileSizeLimitText}MB)`;
      this.propImageFileSizeErrorTxt = `Size up to ${fileSizeLimitText} MB.`;
      this.propImageFileTypeErrorTxt = 'Incorrect format (allow only format file .pdf).';
    }

    if (this.shelfType === ProductTypeEnum.FIX_ASSET) {
      this.propImageAllowedTypes = '(png|jpe?g|jpg)';
      this.propImageSize = 512000;
      this.propImageAllowedExt = '(.jpg|.png|.jpe?g)';
      this.propImageDescriptionTxt = 'Format file .jpg, jpeg, .png (Size up to 500KB)';
      this.propImageFileSizeErrorTxt = 'Size up to 500 KB.';
      this.propImageFileTypeErrorTxt = 'Incorrect Format (allow only format file .jpg, .jpeg, .png).';
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['mode'] &&
      changes['mode'].currentValue &&
      changes['mode'].currentValue === RequestPageModesEnum.REQUEST_EDIT
    ) {
      this.toggleEditShelfInfo();
    }
  }

  get form(): UntypedFormGroup {
    return this.parentForm.get('shelfInfo') as UntypedFormGroup;
  }

  get fileModule() {
    return FileModuleEnum;
  }

  createForm() {
    let initialNull = [];

    if (this.shelfType === ProductTypeEnum.FIX_ASSET) {
      initialNull = [{ value: null, disabled: false }];
    } else {
      initialNull = [{ value: null, disabled: false }, Validators.required];
    }

    return this.fb.group({
      shelfName: [{ value: null, disabled: false }, [Validators.required, NoSpaceOnlyValidation]],
      shelfType: initialNull,
      shelfImage: [{ value: null, disabled: false }, [Validators.required, NoSpaceOnlyValidation]]
    });
  }

  goToShelfType() {
    const modal = this.modalService.show(AddShelfTypeComponent, {
      class: 'modal-lg',
      backdrop: 'static'
    });

    modal.content.addedShelfType.pipe(untilComponentDestroyed(this)).subscribe(() => {
      this.loadShelfTypeList('');
    });
  }

  loadShelfTypeList(initialTerm: string) {
    this.shelfTypeList$ = concat(
      of([]),
      this.shelfTypeSearchInput$.pipe(
        startWith(initialTerm),
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.shelfTypeSearchLoading = true)),
        switchMap(term =>
          this.shelfTypeService
            .getShelfList({
              searchCriteria: term
            })
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => {
                this.shelfTypeSearchLoading = false;
              })
            )
        )
      )
    );
  }

  setShelfFixAssetInfoValue() {
    this.form.disable();

    this.shelfFixAssetRequestView$ = this.localStore.pipe(select(getSelectShelfPage(this.page)));
    this.shelfFixAssetRequestView$
      .pipe(
        untilComponentDestroyed(this),
        filter(value => Boolean(value))
      )
      .subscribe(data => {
        this.status = data.status;
        this.type = data.type;

        if (data.shelfInfo) {
          this.form.patchValue({
            ...data.shelfInfo,
            shelfImage: getFileUploadObj(data.shelfInfo.shelfImage)
          });

          if (data.shelfInfo.changes && data.shelfInfo.changes.length > 0) {
            this.listOfChange = data.shelfInfo.changes.map(change => {
              return change.field;
            });
          }
        }

        if (this.mode === RequestPageModesEnum.REQUEST_EDIT) {
          this.toggleEditShelfInfo();
        }
      });
  }

  setShelfInventoryInfoValue() {
    this.form.disable();

    this.shelfInventoryRequestView$ = this.localStore.pipe(select(getSelectShelfPage(this.page)));
    this.shelfInventoryRequestView$
      .pipe(
        untilComponentDestroyed(this),
        filter(value => Boolean(value))
      )
      .subscribe(data => {
        this.status = data.status;
        this.type = data.type;

        if (data.shelfInfo) {
          this.form.patchValue({
            ...data.shelfInfo,
            shelfImage: getFileUploadObj(data.shelfInfo.shelfImage)
          });

          if (data.shelfInfo.changes && data.shelfInfo.changes.length > 0) {
            this.listOfChange = data.shelfInfo.changes.map(change => {
              return change.field;
            });
          }
          this.uploaded.emit(true);
        }

        if (this.mode === RequestPageModesEnum.REQUEST_EDIT) {
          this.toggleEditShelfInfo();
        }
      });
  }

  setShelfInfoValue() {
    this.form.disable();

    this.shelfRequestView$ = this.localStore.pipe(select(getSelectShelf(this.page)));
    this.shelfRequestView$
      .pipe(
        untilComponentDestroyed(this),
        filter(value => Boolean(value))
      )
      .subscribe(data => {
        this.status = data.status;
        this.type = data.type;

        if (data.shelfInfo) {
          this.form.patchValue({
            ...data.shelfInfo,
            shelfImage: getFileUploadObj(data.shelfInfo.shelfImage)
          });

          if (data.shelfInfo.changes && data.shelfInfo.changes.length > 0) {
            this.listOfChange = data.shelfInfo.changes.map(change => {
              return change.field;
            });
          }

          this.uploaded.emit(true);
        }

        if (this.mode === RequestPageModesEnum.REQUEST_EDIT) {
          this.toggleEditShelfInfo();
        }
      });
  }

  toggleEditShelfInfo() {
    this.mode = RequestPageModesEnum.REQUEST_EDIT;

    if (this.form && ![NewRequestStatusEnum.AWAITING_FIRST_LOT, NewRequestStatusEnum.ACTIVE].includes(this.status)) {
      this.form.enable();
    }
  }

  hasChanges(field: string) {
    return (
      (this.page === ShelfPages.SHELF_REQUEST ||
        this.page === ShelfPages.SHELF_REQUEST_INVENTORY ||
        this.page === ShelfPages.SHELF_REQUEST_FIX_ASSET) &&
      this.type === NewRequestTypeEnum.EDIT &&
      this.listOfChange.includes(field)
    );
  }

  get isShowButton() {
    return (
      this.mode !== RequestPageModesEnum.REQUEST_VIEW &&
      this.status !== NewRequestStatusEnum.AWAITING_FIRST_LOT &&
      this.type !== NewRequestTypeEnum.EDIT
    );
  }

  get isShowShelfType(): boolean {
    return this.shelfType !== ProductTypeEnum.FIX_ASSET;
  }

  showShelfName(isShelfInventory, env): string {
    return isShelfInventory && env ? 'Shelf Option' : 'Shelf Name';
  }

  get env() {
    return environment.prefixCompany !== 'TD';
  }
}
